import { FC } from "react";
import cx from "classnames";
import styles from "./inventory-card.module.scss";
import { Vehicle } from "../../../types/vehicle";
import { ClientSiteData, ShowIfNotSetValues } from "../../../types/site";
import PricePropertyValue from "../vehicle-card/pricePropertyValue";
import CarFavoriteButton from "../vehicle-card/car-favorite-button";
import { getPhoneForVehicle, getVehicleUrl } from "../../../utils/ui";
import vehicleCardStyles from "../vehicle-card/car-card-grid.module.scss";
import { PageContext } from "../../../types/ui";
import { getTranslationModule, Translations } from "../../../utils/translations";
import VehiclePropertyView from "../vehicle-card/vehicle-property-view";
import { CustomTooltipType } from "../vehicle-card/customTooltip/customTooltip";
import { VehicleConditionalCardData } from "../../../types/blocks";
import { vehiclePriceFormatter, visualPriceStyleFromPropertyStyle } from "../../../utils/vehicleProperties";

interface IInventoryCard {
  className?: string;
  vehicle: Vehicle;
  siteData: ClientSiteData;
  vehicleCardData?: VehicleConditionalCardData;
  pageContext: PageContext;
  translations: Translations;
}

const handleOnImageLoadError = (e: any, item: Vehicle) => {
  e.target.src = `/images/brands/${item?.make.toLowerCase()}.png` || "/images/out-of-stock.png";
};

const InventoryCard: FC<IInventoryCard> = ({ vehicle, className, siteData, vehicleCardData, translations }) => {
  const t = getTranslationModule(translations, "common");

  const priceStyle = visualPriceStyleFromPropertyStyle(vehicleCardData?.priceStyle);
  const priceDisclaimer = vehicleCardData?.priceDisclaimer;

  const photos = vehicle?.photosMedium || vehicle.photos;
  const dealerPhoto =
    siteData?.noImagesPhoto === "" || siteData?.noImagesPhoto === undefined
      ? ""
      : siteData?.baseUrl + siteData?.noImagesPhoto;

  const priceTextValue = vehiclePriceFormatter(vehicle, vehicleCardData?.priceField);

  return (
    <div className={cx(styles.container, styles.grid, className)}>
      <div className={styles.header}>
        <span className={styles.type}>{vehicle.type}</span>
        <span className={styles.year}>{vehicle.year}</span>
        <CarFavoriteButton vehicle={vehicle} className={styles.likeButton} translations={translations} />
      </div>
      <div
        className={cx(styles.galery, {
          [styles.galeryLogo]: !photos?.length,
        })}
      >
        <img
          src={photos[0] || dealerPhoto || `/images/brands/${vehicle?.make.toLowerCase()}.png`}
          alt="Car"
          loading={undefined}
          onError={(e) => handleOnImageLoadError(e, vehicle)}
          onClick={() => window.open(getVehicleUrl(vehicle), "_self")}
        />
        {!photos?.length && <span className={styles.noImageText}>{t("photos_coming_soon")}</span>}
      </div>

      <div className={styles.description}>
        <div className={styles.descriptionContainer}>
          <a href={getVehicleUrl(vehicle)} title={`${vehicle.year} ${vehicle.make}`}>
            <h3 className={styles.name}>{`${vehicle.year} ${vehicle.make}`}</h3>
          </a>
          <a href={getVehicleUrl(vehicle)} title={`${vehicle.model} ${vehicle.trim}`}>
            <h3
              className={styles.make}
              title={`${vehicle.model} ${vehicle.trim}`}
            >{`${vehicle.model} ${vehicle.trim}`}</h3>
          </a>
          {vehicle.stock && (
            <span className={styles.stock}>
              {<b>Stock:</b>} {vehicle.stock}
            </span>
          )}
        </div>
        <PricePropertyValue
          propertyValue={priceTextValue}
          phone={getPhoneForVehicle(siteData, vehicle)}
          className={styles.price}
          propertyVisualStyle={priceStyle}
          callToPriceClassName={cx(styles.price, styles.priceSmall)}
          translations={translations}
          disclaimer={priceDisclaimer}
          tooltipType={CustomTooltipType.SRP_GRID_PRICE}
          showIfNotSet={ShowIfNotSetValues.NOTHING}
        />
      </div>
      {vehicleCardData?.infoTab?.properties?.map((item, index) =>
        item.name !== "stock" ? (
          <VehiclePropertyView
            key={index}
            property={item}
            vehicle={vehicle}
            siteData={siteData}
            showPrice={true}
            translations={translations}
            className={vehicleCardStyles.info}
            titleClassName={vehicleCardStyles.infoTitle}
            callToPriceClassName={cx(vehicleCardStyles.price, vehicleCardStyles.priceSmall)}
            valueClassName={vehicleCardStyles.infoValue}
            tooltipType={CustomTooltipType.SRP_GRID_PROPERTY}
          />
        ) : null,
      )}
      <div className={cx(styles.details, styles.detailsInventory)}>
        <a href={getVehicleUrl(vehicle)} title={t("view_details")} className={styles.viewDetails}>
          {t("view_details")}
        </a>
      </div>
    </div>
  );
};

export default InventoryCard;
